import React, { FocusEvent } from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, UseFormReturn } from 'react-hook-form';
import css from './text-area.module.css';
import readInputCss from './read-input.module.css';
import FormField from '../../common/form-field';
import { parseError } from '../../../services/utils';

export default function TextArea(
  name: string,
  label: string = null,
  rows: number = 5,
  horizontal?: boolean,
  className?: string,
  column?: boolean,
  inline: boolean = false,
  placeholder: string = '',
  isNoteForm?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <TextAreaComponent
      name={name}
      label={label}
      rows={rows}
      {...rest}
      horizontal={horizontal}
      className={className}
      column={column}
      inline={inline}
      placeholder={placeholder}
      isNoteForm={isNoteForm}
    />
  );
}

export function TextAreaComponent({
  name,
  label,
  parentName,
  rows,
  disabled,
  readonly,
  required,
  horizontal,
  column,
  className,
  inline,
  placeholder,
  isNoteForm,
}: FormComponentProps & {
  name: string;
  label?: string;
  rows?: number;
  required?: string[];
  horizontal?: boolean;
  className?: string;
  inline?: boolean;
  placeholder?: string;
  isNoteForm?: boolean;
  onBlur?: (
    event: FocusEvent<HTMLTextAreaElement>,
    form: UseFormReturn
  ) => void;
  column?: boolean;
}) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const textAreaName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(textAreaName, errors);

  if (readonly) {
    return (
      <FormField
        inline={inline}
        label={label ? t(label) : t(name)}
        labelFor={textAreaName}
        required={required?.includes(textAreaName)}
        error={error ? error.message : null}
        horizontal={horizontal}
        column={column}
      >
        <Controller
          name={textAreaName}
          defaultValue=""
          render={(props) => (
            <div className={`${css['readonly']} ${readInputCss['readonly']}`}>
              <p data-name={textAreaName}>{props.field.value}</p>
            </div>
          )}
        />
      </FormField>
    );
  } else {
    return (
      <FormField
        inline={inline}
        label={label ? t(label) : t(name)}
        labelFor={textAreaName}
        required={required?.includes(textAreaName)}
        error={error ? error.message : null}
        horizontal={horizontal}
        column={column}
        isNoteForm={isNoteForm}
      >
        <textarea
          name={textAreaName}
          rows={rows}
          disabled={disabled}
          readOnly={readonly}
          maxLength={4000}
          className={
            isNoteForm
              ? error
                ? css['textarea-note-error']
                : css['textarea-note']
              : `${css['textarea']} ${css[className]}`
          }
          placeholder={placeholder}
          {...register(textAreaName)}
        />
      </FormField>
    );
  }
}
